import { useState, useMemo, useCallback } from "react";
import { 
  Target, 
  TrendingUp, 
  CheckCircle2, 
  XCircle,
  Users,
  Clock
} from "lucide-react";
import { Header } from "@/components/dashboard/Header";
import { KPICard } from "@/components/dashboard/KPICard";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { FitScoreChart } from "@/components/dashboard/FitScoreChart";
import { VehicleChart } from "@/components/dashboard/VehicleChart";
import { AgencyChart } from "@/components/dashboard/AgencyChart";
import { TechAreaChart } from "@/components/dashboard/TechAreaChart";
import { DueDateChart } from "@/components/dashboard/DueDateChart";
import { OpportunityTable } from "@/components/dashboard/OpportunityTable";
import { opportunitiesData, getScoreBand } from "@/data/opportunities";
import { toast } from "sonner";

const DEFAULT_FILTERS = {
  vehicle: "All Vehicles",
  agency: "All Agencies",
  tech: "All Technologies",
  scoreBand: "All Scores",
};

export default function Dashboard() {
  const [opportunities, setOpportunities] = useState(opportunitiesData);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Filter the data based on current filters
  const filteredData = useMemo(() => {
    return opportunities.filter(opp => {
      const vehicleMatch = filters.vehicle === "All Vehicles" || 
        opp.vehicle === filters.vehicle ||
        (filters.vehicle === "SAM.gov" && opp.vehicle === "SAM.gov");
      
      const agencyMatch = filters.agency === "All Agencies" || 
        opp.agency === filters.agency;
      
      const techMatch = filters.tech === "All Technologies" || 
        opp.tech === filters.tech;
      
      const scoreBandMatch = filters.scoreBand === "All Scores" || 
        getScoreBand(opp.score) === filters.scoreBand;
      
      return vehicleMatch && agencyMatch && techMatch && scoreBandMatch;
    });
  }, [opportunities, filters]);

  // Calculate KPI metrics
  const metrics = useMemo(() => {
    const total = filteredData.length;
    const avgScore = total > 0 
      ? Math.round(filteredData.reduce((sum, opp) => sum + opp.score, 0) / total)
      : 0;
    const bids = filteredData.filter(opp => opp.recommendation === "Bid").length;
    const noBids = filteredData.filter(opp => opp.recommendation === "No-Bid").length;
    const partners = filteredData.filter(opp => opp.recommendation === "Partner").length;
    const urgent = filteredData.filter(opp => {
      const daysUntilDue = Math.ceil((new Date(opp.due) - new Date("2026-02-15")) / (1000 * 60 * 60 * 24));
      return daysUntilDue <= 14;
    }).length;

    return { total, avgScore, bids, noBids, partners, urgent };
  }, [filteredData]);

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    toast.success("Filters reset successfully");
  }, []);

  // Handle recommendation update
  const handleUpdateRecommendation = useCallback((id, newRecommendation) => {
    setOpportunities(prev => 
      prev.map(opp => 
        opp.id === id ? { ...opp, recommendation: newRecommendation } : opp
      )
    );
    toast.success(`Updated ${id} to ${newRecommendation}`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Page Title */}
        <div className="animate-fade-in">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading text-foreground">
            Executive Dashboard
          </h2>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Real-time overview of federal contracting opportunities and pipeline analytics
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 animate-slide-up">
          <KPICard
            title="Total Opportunities"
            value={metrics.total}
            subtitle="Active pipeline"
            icon={Target}
            variant="primary"
          />
          <KPICard
            title="Avg Fit Score"
            value={metrics.avgScore}
            subtitle="Portfolio average"
            icon={TrendingUp}
            variant="default"
          />
          <KPICard
            title="Bids"
            value={metrics.bids}
            subtitle="Recommended to bid"
            icon={CheckCircle2}
            variant="success"
          />
          <KPICard
            title="No-Bids"
            value={metrics.noBids}
            subtitle="Not recommended"
            icon={XCircle}
            variant="destructive"
          />
          <KPICard
            title="Partner"
            value={metrics.partners}
            subtitle="Teaming opportunities"
            icon={Users}
            variant="warning"
          />
          <KPICard
            title="Urgent"
            value={metrics.urgent}
            subtitle="Due within 14 days"
            icon={Clock}
            variant="destructive"
          />
        </div>

        {/* Filters */}
        <div className="animate-slide-up" style={{ animationDelay: "100ms" }}>
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slide-up" style={{ animationDelay: "150ms" }}>
          <FitScoreChart data={filteredData} />
          <VehicleChart data={filteredData} />
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up" style={{ animationDelay: "200ms" }}>
          <AgencyChart data={filteredData} />
          <TechAreaChart data={filteredData} />
          <DueDateChart data={filteredData} />
        </div>

        {/* Opportunities Table */}
        <div className="animate-slide-up" style={{ animationDelay: "250ms" }}>
          <OpportunityTable 
            data={filteredData}
            onUpdateRecommendation={handleUpdateRecommendation}
          />
        </div>

        {/* Footer */}
        <footer className="pt-8 pb-6 border-t border-border">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>
              Federal Opportunity Intelligence Platform (OIP) • POC Demo
            </p>
            <p>
              Data shown is for demonstration purposes only
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
